<template>
  <div
    v-if="!(!languages && isPreview)"
    ref="languageSettings"
    data-testid="translate-settings"
    class="relative font-inter"
  >
    <TButton @click="opened = !opened">
      <template #icon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 640 640"
          width="14"
          height="14"
        >
          <path
            d="M192 64C209.7 64 224 78.3 224 96L224 128L352 128C369.7 128 384 142.3 384 160C384 177.7 369.7 192 352 192L342.4 192L334 215.1C317.6 260.3 292.9 301.6 261.8 337.1C276 345.9 290.8 353.7 306.2 360.6L356.6 383L418.8 243C423.9 231.4 435.4 224 448 224C460.6 224 472.1 231.4 477.2 243L605.2 531C612.4 547.2 605.1 566.1 589 573.2C572.9 580.3 553.9 573.1 546.8 557L526.8 512L369.3 512L349.3 557C342.1 573.2 323.2 580.4 307.1 573.2C291 566 283.7 547.1 290.9 531L330.7 441.5L280.3 419.1C257.3 408.9 235.3 396.7 214.5 382.7C193.2 399.9 169.9 414.9 145 427.4L110.3 444.6C94.5 452.5 75.3 446.1 67.4 430.3C59.5 414.5 65.9 395.3 81.7 387.4L116.2 370.1C132.5 361.9 148 352.4 162.6 341.8C148.8 329.1 135.8 315.4 123.7 300.9L113.6 288.7C102.3 275.1 104.1 254.9 117.7 243.6C131.3 232.3 151.5 234.1 162.8 247.7L173 259.9C184.5 273.8 197.1 286.7 210.4 298.6C237.9 268.2 259.6 232.5 273.9 193.2L274.4 192L64.1 192C46.3 192 32 177.7 32 160C32 142.3 46.3 128 64 128L160 128L160 96C160 78.3 174.3 64 192 64zM448 334.8L397.7 448L498.3 448L448 334.8z"
            fill="currentColor"
          />
        </svg>
      </template>
      {{ $t("layout.translate") }}
      <template #iconRight>
        <font-awesome-icon :icon="['fas', 'caret-down']" />
      </template>
    </TButton>
    <div
      v-show="opened"
      data-testid="language-dropdown"
      class="absolute left-[0] top-[calc(100%+1px)] z-[10] box-border w-max pt-1"
      :class="{
        'min-w-[150px]': isPreview,
        'min-w-[300px]': !isPreview,
      }"
    >
      <div
        class="box-border w-max min-w-[100%] max-w-full overflow-hidden rounded-md border border-solid border-gray-500 bg-gray-700 p-2 pb-3"
      >
        <h3
          class="mb-2 font-inter text-[13px] font-semibold leading-5 text-gray-50"
        >
          {{ $t("layout.translate_to_language") }}
        </h3>
        <LanguageSelector
          :langs-to-use="filteredLangsToUse"
          @select="translateToLanguage"
          @cancel="opened = false"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import LanguageSelector from "../LanguageSettings/LanguageSelector.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { usePreviewStore } from "@/stores/preview";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useOpenAiStore } from "@/stores/openAiStore";
  import { TopolLanguages } from "@/schemas/template/languageSchema";
  import { IMultilingualLanguage } from "@/schemas/language/languageSchema";

  import { TButton } from "@topol/twig-ui";
  import { iso639 } from "@topol/utils";

  import { onClickOutside } from "@vueuse/core";
  import { computed, ref, onMounted, onBeforeUnmount } from "vue";

  const mjmlStore = useMjmlStore();
  const previewStore = usePreviewStore();
  const generalStore = useGeneralStore();
  const openAiStore = useOpenAiStore();

  const languageSettings = ref<HTMLElement | null>(null);
  const opened = ref(false);

  onClickOutside(languageSettings, closeOnClickOutside, {
    ignore: [".lang-confirm", ".lang-confirm *"],
  });

  const isPreview = computed(() => {
    return previewStore.previewMode;
  });

  const langsToUse = computed<IMultilingualLanguage>(() => {
    if (generalStore.customLanguagePreset) {
      return generalStore.customLanguagePreset;
    }
    return iso639;
  });

  const filteredLangsToUse = computed(() => {
    return langsToUse.value.filter(
      (lang) => !languagesCodes.value.includes(lang.code)
    );
  });

  const languages = computed(() => {
    const availableLanguages = mjmlStore.mjml.langs;
    if (!availableLanguages) return availableLanguages;
    const sorted: TopolLanguages = JSON.parse(
      JSON.stringify(availableLanguages)
    );
    return sorted.sort((a, b) => {
      if (a.primary === b.primary) return 0;
      return a.primary ? -1 : 1;
    });
  });

  const languagesCodes = computed(() => {
    if (!languages.value) return [];
    return languages.value.map((lang) => lang.key);
  });

  function escapeByKey(event: KeyboardEvent) {
    if (event.key === "Escape") {
      opened.value = false;
    }
  }

  function closeOnClickOutside() {
    opened.value = false;
  }

  function translateToLanguage(lang: string) {
    if (!opened.value || !lang || lang.trim() === "") return;
    const langObject = langsToUse.value.find((l) => l.code === lang);
    if (!langObject) return;
    openAiStore.translateText(`${langObject.name} (${langObject.code})`);
    opened.value = false;
  }

  onMounted(() => {
    document.addEventListener("keydown", escapeByKey);
    window.$event.on("iframeClick", closeOnClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", escapeByKey);
    window.$event.off("iframeClick", closeOnClickOutside);
  });
</script>
